import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Button, Spin, Alert, Space, Typography, Modal, Empty } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { debounce } from 'lodash';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useDashboardV2 } from '../../hooks/useDashboardV2';
import { useSelector, useDispatch } from 'react-redux';
import { fetchSensor } from '../../actions/sensor';
import DashboardGrid from './DashboardGrid';
import WidgetWrapper from './WidgetWrapper';
import AddWidgetModal from './AddWidgetModal';
import toast from 'react-hot-toast';
import './DashboardV2.scss';

const { Title } = Typography;
const { confirm } = Modal;

const DashboardV2 = () => {
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [sensores, setSensores] = useState([]);
  const [loadingSensores, setLoadingSensores] = useState(false);
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef(null);

  const {
    layout,
    loading,
    refreshing,
    error,
    addWidget,
    updateWidget,
    updatePositions,
    deleteWidget,
    reload,
    clearError,
  } = useDashboardV2();

  // Obtener sensores del estado Redux
  const sensoresState = useSelector((state) => state.sensorReducer?.sensors || []);

  // Cargar sensores al montar el componente
  useEffect(() => {
    const cargarSensores = async () => {
      try {
        setLoadingSensores(true);
        // Cargar todos los sensores (máximo 1000 para tener todos disponibles)
        await dispatch(fetchSensor(1000, 0));
      } catch (err) {
        console.error('Error cargando sensores:', err);
        toast.error('Error al cargar la lista de sensores');
      } finally {
        setLoadingSensores(false);
      }
    };

    cargarSensores();
  }, [dispatch]);

  // Actualizar sensores cuando cambia el estado
  useEffect(() => {
    // El backend devuelve { total, count, data: [] }
    if (sensoresState && sensoresState.data && Array.isArray(sensoresState.data)) {
      setSensores(sensoresState.data);
    } else if (Array.isArray(sensoresState) && sensoresState.length > 0) {
      // Fallback si viene como array directo
      setSensores(sensoresState);
    } else {
      setSensores([]);
    }
  }, [sensoresState]);

  // Debounce para actualizar posiciones (evitar muchas llamadas al arrastrar)
  const debouncedUpdatePositions = useCallback(
    debounce((widgets) => {
      updatePositions(widgets).catch((err) => {
        console.error('Error updating positions:', err);
        toast.error('Error al actualizar posiciones');
      });
    }, 500),
    [updatePositions]
  );

  // Manejar cambio de posiciones
  const handlePositionChange = useCallback(
    (widgets) => {
      debouncedUpdatePositions(widgets);
    },
    [debouncedUpdatePositions]
  );

  // Manejar agregar o editar widget
  const handleSaveWidget = async (widgetIdOrData, updateData) => {
    try {
      // Si recibimos dos parámetros, estamos editando
      if (updateData !== undefined) {
        const widgetId = widgetIdOrData;
        await updateWidget(widgetId, updateData.config);
        toast.success('Widget actualizado exitosamente');
        setEditingWidget(null);
      } else {
        // Si recibimos un parámetro, estamos creando
        const widgetData = widgetIdOrData;
        await addWidget(widgetData);
        toast.success('Widget agregado exitosamente');
      }
      setShowAddModal(false);
    } catch (err) {
      console.error('Error saving widget:', err);
      toast.error(err.message || 'Error al guardar widget');
      throw err;
    }
  };

  // Manejar eliminar widget
  const handleDeleteWidget = (widgetId) => {
    confirm({
      title: '¿Eliminar widget?',
      icon: <ExclamationCircleOutlined />,
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteWidget(widgetId);
          toast.success('Widget eliminado exitosamente');
        } catch (err) {
          console.error('Error deleting widget:', err);
          toast.error('Error al eliminar widget');
        }
      },
    });
  };

  // Manejar editar widget
  const handleEditWidget = (widget) => {
    setEditingWidget(widget);
    setShowAddModal(true);
  };

  // Manejar exportación a PDF
  const handleExportToPDF = async () => {
    if (!gridRef.current) {
      toast.error('No se encontró el contenido a exportar');
      return;
    }

    try {
      setExporting(true);
      toast.loading('Generando PDF...', { id: 'export-pdf' });

      // Agregar clase temporal para optimizar el renderizado
      const gridElement = gridRef.current;
      gridElement.classList.add('exporting');

      // Esperar un momento para que se apliquen los estilos
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capturar el contenido como imagen
      const canvas = await html2canvas(gridElement, {
        scale: 2, // Mayor calidad
        useCORS: true,
        logging: false,
        backgroundColor: '#f0f2f5',
        windowWidth: gridElement.scrollWidth,
        windowHeight: gridElement.scrollHeight,
      });

      // Crear PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const imgX = (pageWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
      
      // Agregar título
      const dashboardName = layout?.nombre || 'Dashboard Personalizado';
      const timestamp = new Date().toLocaleString('es-ES');
      
      pdf.save(`${dashboardName}_${timestamp.replace(/[/:]/g, '-')}.pdf`);

      gridElement.classList.remove('exporting');
      toast.success('PDF generado exitosamente', { id: 'export-pdf' });
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error('Error al generar el PDF', { id: 'export-pdf' });
      if (gridRef.current) {
        gridRef.current.classList.remove('exporting');
      }
    } finally {
      setExporting(false);
    }
  };

  // Renderizar estado de carga inicial
  if (loading && !layout) {
    return (
      <div className="dashboard-v2-loading">
        <Spin size="large" tip="Cargando dashboard..." />
      </div>
    );
  }

  // Renderizar error
  if (error && !layout) {
    return (
      <div className="dashboard-v2-error">
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Space>
              <Button size="small" onClick={clearError}>
                Cerrar
              </Button>
              <Button size="small" type="primary" onClick={reload}>
                Reintentar
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  return (
    <div className="dashboard-v2">
      {/* Header */}
      <div className="dashboard-v2-header">
        <div className="header-left">
          <Title level={2} className="dashboard-title">
            {layout?.nombre || 'Dashboard Personalizado'}
          </Title>
        </div>
        
        <div className="header-actions">
          <Space>
            <Button
              icon={<FilePdfOutlined />}
              onClick={handleExportToPDF}
              loading={exporting}
              disabled={exporting}
            >
              Exportar PDF
            </Button>
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={reload}
              loading={refreshing}
            >
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setShowAddModal(true)}
            >
              Agregar Widget
            </Button>
          </Space>
        </div>
      </div>

      {/* Error temporal */}
      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={clearError}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Grid de widgets */}
      {layout?.widgets && layout.widgets.length > 0 ? (
        <div ref={gridRef}>
          <DashboardGrid
            layout={layout}
            onPositionChange={handlePositionChange}
          >
            {(widget) => (
              <WidgetWrapper
                key={widget.widgetId}
                widget={widget}
                onDelete={handleDeleteWidget}
                onEdit={handleEditWidget}
              />
            )}
          </DashboardGrid>
        </div>
      ) : (
        <div className="dashboard-v2-empty">
          <Empty
            description={
              <Space direction="vertical" size="small">
                <span>No hay widgets en tu dashboard</span>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowAddModal(true)}
                >
                  Agregar tu primer widget
                </Button>
              </Space>
            }
          />
        </div>
      )}

      {/* Modal para agregar o editar widget */}
      <AddWidgetModal
        visible={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingWidget(null);
        }}
        onSave={handleSaveWidget}
        editWidget={editingWidget}
        sensores={sensores}
        loadingSensores={loadingSensores}
      />
    </div>
  );
};

export default DashboardV2;
